'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useReducedMotionPref } from '@/lib/motion';
import { siteConfig } from '@/data/siteConfig';
import { resolveApiUrl } from '@/data/runtime-data';

const navItems = [
  { href: '/', label: 'Home' },
  { href: '/menu', label: 'Menu' },
  { href: '/about', label: 'Our Story' },
  { href: '/contact', label: 'Visit Us' },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [phoneDisplay, setPhoneDisplay] = useState(siteConfig.contact.phoneDisplay);
  const [phoneHref, setPhoneHref] = useState(siteConfig.contact.phoneHref);
  const { scrollY } = useScroll();
  const shouldReduceMotion = useReducedMotionPref();
  const pathname = usePathname();

  const headerOpacity = useTransform(scrollY, [0, 100], [1, 0.95]);
  const headerY = useTransform(scrollY, [0, 100], [0, -10]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadContact = async () => {
      try {
        const res = await fetch(resolveApiUrl('/api/contact'), { cache: 'no-store' });
        if (!res.ok) return;
        const json = (await res.json()) as { phoneDisplay?: string; phoneHref?: string };
        if (json.phoneDisplay) setPhoneDisplay(json.phoneDisplay);
        if (json.phoneHref) setPhoneHref(json.phoneHref);
      } catch (error) {
        console.warn('Failed to load live contact info, using defaults.', error);
      }
    };
    void loadContact();
  }, []);

  const headerVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  return (
    <motion.header
      variants={shouldReduceMotion ? {} : headerVariants}
      initial="hidden"
      animate="visible"
      style={{
        opacity: shouldReduceMotion ? 1 : headerOpacity,
        y: shouldReduceMotion ? 0 : headerY,
      }}
      className={`sticky top-3 sm:top-4 z-40 mx-4 sm:mx-6 lg:mx-8 transition-all duration-300 rounded-2xl overflow-hidden ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md border border-amber-100 shadow-lg'
          : 'bg-white/90 backdrop-blur-sm border border-amber-50 shadow-sm'
      }`}
    >
      <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-400 to-amber-600" />
      <div className="container mx-auto px-6 py-2">
        <div className="flex items-center justify-between h-14">
          <Link href="/" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 relative">
              <Image
                src="/unchai-logo.png"
                alt="Unchai Thai Restaurant logo"
                fill
                sizes="40px"
                className="object-contain"
                priority
              />
            </div>
            <span className="font-semibold text-lg text-gray-900">Unchai Thai Restaurant</span>
          </Link>

          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navItems.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`text-sm font-semibold transition-colors duration-200 whitespace-nowrap px-1 py-1 rounded-lg ${
                    active
                      ? 'text-black bg-amber-50'
                      : 'text-gray-600 hover:text-black focus-visible:text-black'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center space-x-3">
            <Link href={phoneHref} className="px-3 py-2 rounded-full bg-amber-50 text-amber-900 font-semibold text-sm border border-amber-200 hover:border-amber-400">
              Call {phoneDisplay}
            </Link>
            <Link href="/menu" className="btn btn-primary px-5 py-2.5 text-sm">
              Order / Menu
            </Link>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-3 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-amber-100 bg-white"
          >
            <nav className="py-4 space-y-2 max-h-[70vh] overflow-y-auto">
              {navItems.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={`mobile-${item.href}`}
                    href={item.href}
                    className={`block px-4 py-3 rounded-xl text-base transition-colors duration-200 ${
                      active
                        ? 'bg-amber-50 text-black border border-amber-100'
                        : 'text-gray-700 hover:text-black hover:bg-amber-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                );
              })}

              <div className="px-4 pt-4 border-t border-amber-100 space-y-3">
                <Link
                  href="/menu"
                  className="block w-full text-center btn btn-primary"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Order / Menu
                </Link>
                <Link
                  href={siteConfig.contact.phoneHref}
                  className="block w-full text-center font-semibold text-gray-800 hover:text-black px-4 py-3 rounded-xl bg-amber-50 border border-amber-100"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Call {siteConfig.contact.phoneDisplay}
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
}
